// Which archived chat lines belong on screen at a given playback position, and how a live
// tail merges with a fetched window.
//
// Pure and separately tested, following the same split as player/tapHold.ts: the component
// renders, this decides. vitest runs with no DOM, so logic that lives here is testable and
// logic that lives in the .tsx file is not.

import type { ChatMessage, TimelineGap } from "../api/types";

/**
 * What the panel is showing.
 *
 * `live` tails the channel as it happens. `replay` follows the playhead through the archive,
 * which is a different thing from "recent": scrubbing to a show from last Tuesday should show
 * what was said during that show, not what is being said now.
 */
export type ChatMode = "live" | "replay";

export interface ReplayWindow {
  startMs: number;
  endMs: number;
}

/**
 * The window of chat to fetch and display for a playback position.
 *
 * Quantised, for the same reason the timeline's fetch window is: playback advances every
 * frame, and a window keyed on the exact position would issue a request per second and never
 * hit the query cache. `stepMs` is the quantum, and the window is anchored to a multiple of
 * it so consecutive positions inside one step produce byte-identical query keys.
 */
export function replayWindow(
  positionMs: number,
  offsetSeconds: number,
  leadMs: number,
  stepMs: number,
): ReplayWindow {
  // The offset moves the window earlier: a line typed at T reacts to audio from before T, so
  // audio at T is discussed slightly after T.
  const centre = positionMs + offsetSeconds * 1000;
  const anchor = Math.floor(centre / stepMs) * stepMs;
  return { startMs: anchor - leadMs, endMs: anchor + stepMs + leadMs };
}

/**
 * Lines that have "already happened" at this playback position.
 *
 * Replay is a transcript catching up to the playhead rather than a window centred on it:
 * showing what is about to be said would spoil a rewind, and it reads as the panel being out
 * of sync. Lines land as the audio that prompted them plays.
 */
export function visibleAt(
  messages: readonly ChatMessage[],
  positionMs: number,
  offsetSeconds: number,
): ChatMessage[] {
  const cutoff = positionMs + offsetSeconds * 1000;
  return messages.filter((message) => Date.parse(message.ts) <= cutoff);
}

/**
 * Merge a fetched window with lines that arrived over the live stream.
 *
 * Deduplicated by id, because the two overlap by design: a message can arrive on the stream
 * and then again in the next window fetch, and rendering it twice is the visible bug. Sorted
 * by timestamp with id as the tie-break, so two lines in the same millisecond keep the order
 * the server assigned rather than an arbitrary one.
 */
export function mergeMessages(
  fetched: readonly ChatMessage[],
  live: readonly ChatMessage[],
): ChatMessage[] {
  const byId = new Map<number, ChatMessage>();
  for (const message of fetched) byId.set(message.id, message);
  // Live wins on collision: it is the same row, and if a redaction happened in between the
  // fresher copy is the honest one.
  for (const message of live) byId.set(message.id, message);

  return [...byId.values()].sort((a, b) => {
    const delta = Date.parse(a.ts) - Date.parse(b.ts);
    return delta !== 0 ? delta : a.id - b.id;
  });
}

/** Keep the tail bounded so a long session does not grow an unbounded array. */
export function trimToLast<T>(messages: readonly T[], limit: number): T[] {
  return messages.length <= limit ? [...messages] : messages.slice(messages.length - limit);
}

/**
 * True when this instant falls in a stretch the logger was not connected for.
 *
 * The panel says so explicitly. "Nobody spoke" and "nobody was listening" look identical
 * otherwise, and only one of them is a fact about the channel.
 */
export function inGap(gaps: readonly TimelineGap[], atMs: number): boolean {
  return gaps.some((gap) => Date.parse(gap.start) <= atMs && atMs < Date.parse(gap.end));
}

/**
 * Total unlogged time inside a window, in seconds.
 *
 * Used to caption a window that is partly or wholly uncovered, so an empty panel can explain
 * itself rather than looking broken.
 */
export function gapSeconds(gaps: readonly TimelineGap[]): number {
  return gaps.reduce((total, gap) => total + gap.seconds, 0);
}

export interface ChatLine {
  message: ChatMessage;
  /** Consecutive lines from one nick drop the repeated name. */
  showNick: boolean;
}

/**
 * Annotate a transcript for rendering.
 *
 * Grouping consecutive lines by the same speaker is what makes an IRC log readable rather
 * than a column of repeated names. A kind change breaks the group: "/me waves" reads as a
 * different kind of utterance and should carry its nick even mid-run.
 */
export function toLines(messages: readonly ChatMessage[]): ChatLine[] {
  let previousNick: string | null = null;
  let previousKind: string | null = null;
  return messages.map((message) => {
    const showNick = message.nick !== previousNick || message.kind !== previousKind;
    previousNick = message.nick;
    previousKind = message.kind;
    return { message, showNick };
  });
}
